import { Avatar, Modal, Paper } from '@mui/material';
import { IVerifyOTPCode, otpCodeValidation } from 'API/OTPLogin/OTPLogin';
import { OTPLoginContext } from 'Context/OTPLoginContext';
import {
  useAppSelector,
  useDictionary,
  useDispatchApp,
  useIsDesktop,
} from 'Hooks';
import { CirclePurpleCloseButton } from 'Images';
import { setOTPLogin } from 'Store/OTPLoginSlice';
import { useCallback, useContext, useState } from 'react';
import { CodeValidation } from './CodeValidation';
import { CustomMetaTitle } from 'Components';
import { Dictionary } from 'Dictionary';
import { DICTIONARY } from 'Enum';
import { onEnterOrEscapeKeyDown } from 'Utils/accessibilityOnKeyDown';

interface IOTPCodeValidationWrapper {
  isEmail: boolean;
  setValidationModal: Function;
  sendCodeAgain: Function;
}

export const OTPCodeValidationWrapper = ({
  isEmail,
  setValidationModal,
  sendCodeAgain,
}: IOTPCodeValidationWrapper) => {
  const { userDetails, setUserDetails } = useContext(OTPLoginContext);
  const [isValidated, setIsValidated] = useState<boolean | null>(null);
  const [isBlocked, setIsBlocked] = useState<boolean>(false);
  const dispatch = useDispatchApp();

  const dictionary = useDictionary(
    Dictionary,
    DICTIONARY.OTP_Login
  ).phoneEmailValidation;
  const isDesktop = useIsDesktop();

  const { generalOptions } = useAppSelector((state) => state.generalOptions);

  const validationHandler = useCallback(
    async (code: string) => {
      const requestBody: IVerifyOTPCode = {
        userIsrId: userDetails.IsrId,
        otpCode: code,
      };
      if (isEmail) requestBody['emailAddress'] = userDetails.email;
      else
        requestBody['phone'] =
          userDetails.phonePrefix + userDetails.phoneNumber;
      const result = await otpCodeValidation(requestBody);
      if (result.status === 410) {
        setIsBlocked(true);
        setIsValidated(false);
        return;
      }
      if (result.data) {
        const resultData = result.data;
        setIsValidated(true);
        setUserDetails({
          ...userDetails,
          token: resultData.accessToken,
          refreshToken: resultData.refreshToken,
        });
        dispatch(
          setOTPLogin({
            ...userDetails,
            phonePrefix: generalOptions?.general?.phonePrefixes.find(
              (prefix) => prefix.value === userDetails.phonePrefix
            )?.id,
            token: resultData.accessToken,
            refreshToken: resultData.refreshToken,
          })
        );
        window.sessionStorage.setItem('token', resultData.accessToken);
      } else setIsValidated(false);
    },
    [dispatch, generalOptions, isEmail, setUserDetails, userDetails]
  );

  return (
    <>
      <CustomMetaTitle
        disablePrefix
        title={
          Dictionary[DICTIONARY.OTP_Login].metaTitle +
          ' - ' +
          Dictionary[DICTIONARY.OTP_Login].metaTitleStep.stepFour
        }
      />
      <Modal
        open={true}
        BackdropProps={{
          sx: { bgcolor: '#081E54', opacity: '0.8 !important' },
        }}
      >
        <Paper
          sx={{
            position: 'absolute',
            left: '50%',
            top: '130px',
            transform: 'translateX(-50%)',
            bgcolor: '#E7EDFF',
            maxWidth: '770px',
            width: isDesktop ? '100%' : '85%',
            display: 'flex',
            flexFlow: 'column',
            alignItems: 'center',
            padding: isDesktop ? '25px 20px 52px 20px' : '16px 16px 120px 16px',
            fontFamily: 'Assistant',
          }}
        >
          <Avatar
            alt=''
            aria-label={dictionary.closeModal}
            src={CirclePurpleCloseButton}
            sx={{
              mb: '16px',
              marginLeft: 'auto',
              width: 'fit-content',
              height: '40px',
              border: 'none',
              bgcolor: 'transparent',
              ':hover': {
                cursor: 'pointer',
              },
            }}
            tabIndex={0}
            role='button'
            component='button'
            onKeyDown={(e: React.KeyboardEvent) => onEnterOrEscapeKeyDown(e)}
            onClick={() => setValidationModal(false)}
          />
          <CodeValidation
            isValidated={isValidated}
            setIsValidated={setIsValidated}
            validationHandler={validationHandler}
            setValidationModal={setValidationModal}
            sendCodeAgain={sendCodeAgain}
            isBlocked={isBlocked}
            setIsBlocked={setIsBlocked}
          />
        </Paper>
      </Modal>
    </>
  );
};
